import styled from '@emotion/styled'
import { UpdateUserInformationBodyDto } from '@pharmac-extranet/extranet-api'
import {
  ControlledMultiSelect,
  ExtranetContainer,
  MediaQuery,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacButton, PharmacH3, PharmacMainLogoMono, PharmacText } from '@pharmac/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useCurrentUser } from '../../features/users/useCurrentUser'
import { extranetUsersQueryKeyFactory } from '../../features/users/users.queries'
import { api } from '../../services/HttpService'

const ViewRoot = styled(ExtranetContainer)`
  background: linear-gradient(130.84deg, rgba(136, 40, 143, 0.83) 65.47%, #66d0d5 99.95%);

  height: 100%;
  position: relative;

  ${MediaQuery.maxTablet} {
    width: 100%;
    padding: 0 var(--spacing-large);
  }
`

const LogoContainer = styled.div`
  position: absolute;
  bottom: 50px;
  right: 50px;
`

type Props = {
  setShowRoleModal: () => void
}

export const AdminProfessionalRoleSelectModal = ({ setShowRoleModal }: Props) => {
  const { t, key } = useLanguage()
  const queryClient = useQueryClient()
  const { minDesktop } = useBreakpoint()
  const { data } = useCurrentUser()

  const formhook = useForm<UpdateUserInformationBodyDto>({
    mode: 'onChange',
    defaultValues: {
      professionalRoleIds: [],
      professionalRolesSelected: true,
    },
  })

  useEffect(() => {
    if (data) {
      formhook.reset({
        professionalRoleIds: (data.professionalRoles || []).map((role) => role.id),
        professionalRolesSelected: true,
      })
    }
  }, [data])

  const { data: professionalRoles } = useQuery({
    queryKey: ['__professionalRoles'],
    queryFn: async () => await api.customers.geUserProfessionalRoles(),
  })

  const { mutate: updateUserInfo } = useMutation({
    mutationFn: (dto: UpdateUserInformationBodyDto) => api.users.updateUserInformation(dto),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: extranetUsersQueryKeyFactory.getCurrentUser(),
      })
      setShowRoleModal()
    },
  })

  const handleUpdateUserInfo = (dto: UpdateUserInformationBodyDto) => {
    updateUserInfo(dto)
  }

  return (
    <ViewRoot
      full
      flexColumn
      alignCenter
      justifyCenter
    >
      <div
        style={{
          width: minDesktop ? '395px' : 'fit-content',
          height: minDesktop ? '457px' : '75%',
          marginBottom: minDesktop ? '' : '5rem',
          borderRadius: '21px',
          overflow: 'hidden',
        }}
      >
        <ExtranetContainer
          full
          flexColumn
          alignCenter
          style={{
            height: minDesktop ? '100%' : '95%',
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--color-primary) var(--color-accent-2)',
            backgroundColor: 'var(--color-accent-2)',
            borderRadius: '18px',
          }}
        >
          <FormProvider {...formhook}>
            <ExtranetContainer
              full
              flexColumn
              style={{ height: '75%' }}
            >
              <ExtranetContainer
                full
                flexColumn
                alignCenter
                style={{ height: '50%' }}
              >
                <PharmacH3
                  paddingLargeVertical
                  paddingLargeLeft={!minDesktop}
                  color="purple"
                >
                  {t('extranet:inviteLinks.registration.selectProfessionalRoleTitle')}
                </PharmacH3>
                <PharmacText
                  paddingLargeHorizontal
                  paddingLargeBottom
                >
                  {t('extranet:inviteLinks.registration.selectProfessionalRoleInfo')}
                </PharmacText>
              </ExtranetContainer>

              <ExtranetContainer
                full
                alignCenter
                justifyCenter
                style={{ height: '50%' }}
              >
                <div style={{ width: '80%', paddingTop: 'var(--spacing-medium)' }}>
                  <ControlledMultiSelect
                    control={formhook.control as unknown as Control<FieldValues>}
                    name="professionalRoleIds"
                  >
                    {professionalRoles?.map((role) => (
                      <option
                        key={role.id}
                        value={role.id}
                      >
                        {key(role.displayName)}
                      </option>
                    ))}
                  </ControlledMultiSelect>
                </div>
              </ExtranetContainer>
            </ExtranetContainer>

            <ExtranetContainer
              style={{ borderTop: '2px dashed var(--color-accent-4)', height: '25%' }}
              paddingLarge
              full
              justifyCenter={minDesktop}
              spaceBetween={!minDesktop}
            >
              <PharmacButton
                fullWidth={minDesktop}
                variant="secondary"
                marginLargeRight={minDesktop}
                marginSmallRight={!minDesktop}
                onClick={formhook.handleSubmit(handleUpdateUserInfo)}
              >
                {t('extranet:inviteLinks.registration.noSelectedProfessionalRole')}
              </PharmacButton>
              <PharmacButton
                fullWidth={minDesktop}
                disabled={!formhook.formState.isValid || !formhook.formState.isDirty}
                onClick={formhook.handleSubmit(handleUpdateUserInfo)}
              >
                {t('common:save')}
              </PharmacButton>
            </ExtranetContainer>
          </FormProvider>
        </ExtranetContainer>
      </div>
      <LogoContainer>
        <PharmacMainLogoMono
          width="250px"
          color="white"
        />
      </LogoContainer>
    </ViewRoot>
  )
}
